<template>
  <section v-if="work?.length" class="work pe-section">
    <h3>
      {{ workTitle || 'Work Experience' }}
      <SectionActions v-show="isPowerEdit" class="actions" section="work" />
    </h3>
    <template v-for="(exp, index) in cOrderedJobs" :key="exp.name">
      <section
        v-if="exp.position || exp.summary || exp.name"
        class="experience"
      >
        <ItemActions
          v-show="isPowerEdit"
          class="itemActions"
          :index="index"
          section="work"
          :uuid="exp.uuid"
          :actions="['edit', 'delete']"
        />
        <section class="title">
          <div class="details">
            <div class="position">
              <div
                class="jobtitle"
                v-editable="isPowerEdit"
                :data-target-path="`resume.work.${index}.position`"
              >
                {{ exp.position }}
              </div>
              <div class="company">
                <a :href="getUrl(exp.url)" target="_blank">
                  <span
                    @click.prevent
                    v-editable="isPowerEdit"
                    :data-target-path="`resume.work.${index}.name`"
                    v-text="exp.name"
                    class="no-highlights"
                  ></span>
                </a>
                <span v-if="exp.location"> | </span>
                <span
                  v-editable="isPowerEdit"
                  :data-target-path="`resume.work.${index}.location`"
                  v-if="exp.location"
                  v-text="exp.location"
                ></span>
              </div>
            </div>
            <div class="location"></div>
          </div>
          <div class="dates">
            <div>
              <DateDisplay
                :date="exp.startDate"
                :updatePath="`resume.work.${index}.startDate`"
                :isEditable="isPowerEdit"
                :isInvalid="!checkDates(exp.startDate, exp.endDate)"
                @mouseenter="
                  $renderTooltip(
                    $event,
                    'Invalid date range',
                    !checkDates(exp.startDate, exp.endDate)
                  )
                "
              />
              <span v-if="exp.startDate"> - </span>
              <DateDisplay
                :date="exp.endDate"
                hasCurrentOption
                :updatePath="`resume.work.${index}.endDate`"
                :isEditable="isPowerEdit"
                :isInvalid="!checkDates(exp.startDate, exp.endDate)"
                @mouseenter="
                  $renderTooltip(
                    $event,
                    'Invalid date range',
                    !checkDates(exp.startDate, exp.endDate)
                  )
                "
              />
            </div>
          </div>
        </section>
        <section class="description">
          <div
            v-if="exp.summary"
            class="summary"
            v-word-count
            v-editable="isPowerEdit"
            :data-target-path="`resume.work.${index}.summary`"
            v-text="exp.summary"
            @click="
              evt =>
                openRephrasePopup(`resume.work.${index}.summary`, evt.target)
            "
          ></div>
          <Highlights
            v-if="exp.highlights"
            :highlights="exp.highlights"
            :targetPath="`resume.work.${index}.highlights`"
          />
        </section>
      </section>
    </template>
  </section>
  <section class="print:hidden" v-else-if="isPowerEdit">
    <button class="addSectionButton" @click="addWorkItem">
      <FontAwesomeIcon :icon="['fas', 'plus']" class="icon" />
      <span>Add work experience</span>
    </button>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, nextTick } from 'vue'
import { Resume } from '@/types/resume/v1'
import DateDisplay from './support/DateDisplay.vue'
import { useStructuredResumeStore } from '@/store/pinia/structuredResume'
import {
  GlobalEvents,
  useGlobalListeners,
} from '@/composables/useGlobalListeners'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import SectionActions from './SectionActions.vue'
import ItemActions from './ItemActions.vue'
import Highlights from './Highlights.vue'
import sortItems from '@/lib/dates/dateRangeSorter'
import { checkDates } from '@/lib/dates/dateHelper'
import { usePowerEditStore } from '@/store/pinia/powerEdit'

export default defineComponent({
  name: 'Work',
  components: {
    DateDisplay,
    FontAwesomeIcon,
    SectionActions,
    ItemActions,
    Highlights,
  },
  props: {
    work: {
      type: Object as PropType<Resume.Work[]>,
      required: false,
    },
    workTitle: {
      type: String as PropType<string>,
      required: false,
    },
  },
  setup(props) {
    const store = useStructuredResumeStore()
    const { publishEvent } = useGlobalListeners()
    const powerEditStore = usePowerEditStore()
    const { closePopup, openRephrasePopup } = powerEditStore

    const cOrderedJobs = computed(() => {
      return sortItems(props.work || [])
    })

    const getUrl = (url: string): string => {
      if (url && !url.startsWith('http')) {
        return `http://${url}`
      }
      return url
    }

    const addWorkItem = async () => {
      const uuid = store.addSectionItem('work')
      await nextTick()
      publishEvent(GlobalEvents.PE_EDIT_SECTION, {
        section: 'work',
        uuid,
      })
    }

    return {
      isPowerEdit: store.isPowerEdit,
      getUrl,
      cOrderedJobs,
      addWorkItem,
      checkDates,
      closePopup,
      openRephrasePopup,
    }
  },
})
</script>

<style lang="postcss" scoped>
.work {
  @apply text-base-em leading-[inherit] pt-6-em block;
  a {
    @apply text-inherit cursor-text;
  }
  h3 {
    @apply leading-none text-xl-em flex items-center;
    .actions {
      @apply hidden;
    }
    &:hover {
      .actions {
        @apply flex;
      }
    }
  }
  .experience {
    @apply pt-4-em relative;
    .itemActions {
      @apply hidden;
    }
    &:hover {
      .itemActions {
        @apply flex;
      }
    }
    .title {
      @apply flex flex-row justify-between items-start mb-2-em;
      .details {
        @apply mb-2-em;
        .jobtitle {
          @apply font-semibold text-base-em leading-normal;
        }
        .company {
          @apply italic text-sm-em;
          a[href]:not([href='']) {
            @apply text-jsBlue-60 underline;
          }
        }
      }
    }
    .dates {
      @apply text-sm-em;
    }
    .description {
      @apply text-sm-em leading-[inherit];
      .summary {
        @apply mb-2-em whitespace-pre-wrap;
      }
      .highlights {
        ul {
          @apply list-disc ml-4-em list-outside;
        }
      }
    }
  }
}
</style>
